import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  TrendingUp, TrendingDown, DollarSign, Users, Target, AlertCircle, 
  CheckCircle, Clock, BarChart3, Activity, Briefcase, Phone,
  Shield, Megaphone, Eye, ChevronRight, ArrowUp, ArrowDown,
  Calendar, Loader2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NewCEODashboard = () => {
  const navigate = useNavigate();
  const [period, setPeriod] = useState('month');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    loadDashboardData();
  }, [period]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const response = await axios.get(`${API}/executive/ceo/dashboard?period=${period}`, { headers });
      setData(response.data.data);
      setAlerts(response.data.alerts || []);
    } catch (error) {
      console.error('Error loading CEO dashboard:', error);
      if (error.response?.status === 403) {
        toast.error('Access restricted to executive roles only');
        navigate('/dashboard');
      } else {
        toast.error('Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (value === undefined || value === null) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatPercent = (value) => {
    if (value === undefined || value === null) return '0%';
    return `${value.toFixed(1)}%`;
  };

  const getChangeIndicator = (value) => {
    if (!value || value === 0) return null;
    const isPositive = value > 0;
    return (
      <div className={`flex items-center gap-1 text-sm ${isPositive ? 'text-green-500' : 'text-red-500'}`}>
        {isPositive ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
        <span>{Math.abs(value).toFixed(1)}%</span>
      </div>
    );
  };

  const getAlertBadgeVariant = (priority) => {
    switch (priority) {
      case 'critical': return 'destructive';
      case 'high': return 'default';
      default: return 'secondary';
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
            <p className="text-muted-foreground">Loading CEO Dashboard...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 p-6" data-testid="ceo-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Eye className="h-8 w-8 text-primary" />
              CEO Command Center
            </h1>
            <p className="text-muted-foreground mt-1">
              Company-wide strategic oversight and KPIs
            </p>
          </div>
          <div className="flex gap-2">
            {['today', 'week', 'month', 'quarter', 'ytd'].map((p) => (
              <Button
                key={p}
                variant={period === p ? 'default' : 'outline'}
                onClick={() => setPeriod(p)}
                size="sm"
              >
                {p === 'ytd' ? 'YTD' : p.charAt(0).toUpperCase() + p.slice(1)}
              </Button>
            ))}
          </div>
        </div>

        {/* Critical Alerts */}
        {alerts.length > 0 && (
          <Card className="border-l-4 border-l-red-500 bg-red-50 dark:bg-red-950/20">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-red-700 dark:text-red-400">
                <AlertCircle className="h-5 w-5" />
                Attention Required
                <Badge variant="destructive">{alerts.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {alerts.slice(0, 3).map((alert, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-white dark:bg-slate-800 rounded-lg shadow-sm">
                    <div className="flex items-center gap-3">
                      <Badge variant={getAlertBadgeVariant(alert.priority)}>
                        {alert.category}
                      </Badge>
                      <div>
                        <p className="font-medium text-sm">{alert.title}</p>
                        <p className="text-xs text-muted-foreground">{alert.message}</p>
                      </div>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => alert.link && navigate(alert.link)}
                    >
                      {alert.action || 'Review'}
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Revenue & Financial Health */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-200 dark:border-blue-800">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Revenue (MTD)</p>
                  <p className="text-2xl font-bold">{formatCurrency(data?.revenue?.total_revenue_mtd)}</p>
                  {getChangeIndicator(data?.revenue?.revenue_change_percent)}
                </div>
                <div className="h-12 w-12 rounded-full bg-blue-500/20 flex items-center justify-center">
                  <DollarSign className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-500/10 to-green-600/5 border-green-200 dark:border-green-800">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Gross Margin</p>
                  <p className="text-2xl font-bold">{formatPercent(data?.revenue?.gross_margin_percent)}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {formatCurrency(data?.revenue?.gross_margin_amount)}
                  </p>
                </div>
                <div className="h-12 w-12 rounded-full bg-green-500/20 flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border-purple-200 dark:border-purple-800">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Pipeline Value</p>
                  <p className="text-2xl font-bold">{formatCurrency(data?.sales?.pipeline_value)}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {data?.sales?.pipeline_jobs_count || 0} opportunities
                  </p>
                </div>
                <div className="h-12 w-12 rounded-full bg-purple-500/20 flex items-center justify-center">
                  <Target className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-orange-500/10 to-orange-600/5 border-orange-200 dark:border-orange-800">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Conversion Rate</p>
                  <p className="text-2xl font-bold">{formatPercent(data?.sales?.conversion_rate)}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {data?.sales?.total_leads || 0} total leads
                  </p>
                </div>
                <div className="h-12 w-12 rounded-full bg-orange-500/20 flex items-center justify-center">
                  <Activity className="h-6 w-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Department KPIs Tabs */}
        <Tabs defaultValue="sales" className="space-y-4">
          <TabsList className="grid w-full grid-cols-5 lg:w-auto lg:inline-flex">
            <TabsTrigger value="sales" className="gap-2">
              <Target className="h-4 w-4" />
              Sales
            </TabsTrigger>
            <TabsTrigger value="operations" className="gap-2">
              <Briefcase className="h-4 w-4" />
              Operations
            </TabsTrigger>
            <TabsTrigger value="call_center" className="gap-2">
              <Phone className="h-4 w-4" />
              Call Center
            </TabsTrigger>
            <TabsTrigger value="marketing" className="gap-2">
              <Megaphone className="h-4 w-4" />
              Marketing
            </TabsTrigger>
            <TabsTrigger value="eos" className="gap-2">
              <Shield className="h-4 w-4" />
              EOS
            </TabsTrigger>
          </TabsList>

          {/* Sales Tab */}
          <TabsContent value="sales">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5" />
                  Sales Performance
                </CardTitle>
                <CardDescription>Lead generation, pipeline, and conversion metrics</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">New Leads</p>
                    <p className="text-2xl font-bold">{data?.sales?.new_leads || 0}</p>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Active Leads</p>
                    <p className="text-2xl font-bold">{data?.sales?.active_leads || 0}</p>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Avg Deal Size</p>
                    <p className="text-2xl font-bold">{formatCurrency(data?.sales?.avg_deal_size)}</p>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Weighted Pipeline</p>
                    <p className="text-2xl font-bold">{formatCurrency(data?.sales?.weighted_pipeline)}</p>
                  </div>
                </div>
                <div className="mt-4 flex justify-end">
                  <Button variant="outline" onClick={() => navigate('/sales/dashboard')}>
                    View Sales Hub
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Operations Tab */}
          <TabsContent value="operations">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Briefcase className="h-5 w-5" />
                  Operations Performance
                </CardTitle>
                <CardDescription>Production, jobs, and delivery metrics</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Active Projects</p>
                    <p className="text-2xl font-bold">{data?.operations?.active_projects || 0}</p>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Completed Jobs</p>
                    <p className="text-2xl font-bold">{data?.operations?.completed_jobs || 0}</p>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">On-Time Rate</p>
                    <p className="text-2xl font-bold">{formatPercent(data?.operations?.on_time_rate)}</p>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Avg Cycle Time</p>
                    <p className="text-2xl font-bold">{data?.operations?.avg_cycle_time_days || 0} days</p>
                  </div>
                </div>
                <div className="mt-4 flex justify-end">
                  <Button variant="outline" onClick={() => navigate('/operations/dashboard')}>
                    View Operations Hub
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Call Center Tab */}
          <TabsContent value="call_center">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Phone className="h-5 w-5" />
                  Call Center Performance
                </CardTitle>
                <CardDescription>Call metrics, response times, and conversions</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Total Calls</p>
                    <p className="text-2xl font-bold">{data?.call_center?.total_calls || 0}</p>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Missed Calls</p>
                    <p className="text-2xl font-bold text-red-600">{data?.call_center?.missed_calls || 0}</p>
                    <p className="text-xs text-muted-foreground">{formatPercent(data?.call_center?.missed_call_rate)} rate</p>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Avg Response</p>
                    <p className="text-2xl font-bold">{data?.call_center?.avg_response_time_seconds || 0}s</p>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Conversion Rate</p>
                    <p className="text-2xl font-bold">{formatPercent(data?.call_center?.call_conversion_rate)}</p>
                  </div>
                </div>
                <div className="mt-4 flex justify-end">
                  <Button variant="outline" onClick={() => navigate('/call-center/dashboard')}>
                    View Call Center Hub
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Marketing Tab */}
          <TabsContent value="marketing">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Megaphone className="h-5 w-5" />
                  Marketing Performance
                </CardTitle>
                <CardDescription>Campaigns, lead sources, and ROI</CardDescription>
              </CardHeader>
              <CardContent>
                {data?.marketing?.data_status === 'limited' ? (
                  <div className="text-center py-8">
                    <Megaphone className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                    <p className="text-muted-foreground">Marketing campaign data coming soon</p>
                    <p className="text-sm text-muted-foreground mt-2">Lead source tracking is available</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 bg-muted/50 rounded-lg">
                      <p className="text-sm text-muted-foreground">Active Campaigns</p>
                      <p className="text-2xl font-bold">{data?.marketing?.active_campaigns || 0}</p>
                    </div>
                    <div className="p-4 bg-muted/50 rounded-lg">
                      <p className="text-sm text-muted-foreground">Leads Generated</p>
                      <p className="text-2xl font-bold">{data?.marketing?.leads_generated || 0}</p>
                    </div>
                    <div className="p-4 bg-muted/50 rounded-lg">
                      <p className="text-sm text-muted-foreground">Cost per Lead</p>
                      <p className="text-2xl font-bold">{data?.marketing?.cost_per_lead || 'N/A'}</p>
                    </div>
                    <div className="p-4 bg-muted/50 rounded-lg">
                      <p className="text-sm text-muted-foreground">Campaign ROI</p>
                      <p className="text-2xl font-bold">{data?.marketing?.campaign_roi || 'N/A'}</p>
                    </div>
                  </div>
                )}
                <div className="mt-4 flex justify-end">
                  <Button variant="outline" onClick={() => navigate('/marketing/dashboard')}>
                    View Marketing Hub
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* EOS Tab */}
          <TabsContent value="eos">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  EOS System Health
                </CardTitle>
                <CardDescription>Rocks, issues, scorecard, and L10 meetings</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Rocks On Track</p>
                    <p className="text-2xl font-bold text-green-600">{data?.eos?.rocks?.on_track || 0}</p>
                    <p className="text-xs text-muted-foreground">of {data?.eos?.rocks?.total || 0} total</p>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Open Issues</p>
                    <p className="text-2xl font-bold text-orange-600">{data?.eos?.issues?.open || 0}</p>
                    <p className="text-xs text-muted-foreground">{formatPercent(data?.eos?.issues?.resolution_rate)} resolved</p>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Scorecard Hit Rate</p>
                    <p className="text-2xl font-bold">{formatPercent(data?.eos?.scorecard?.target_hit_rate)}</p>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">L10s This Month</p>
                    <p className="text-2xl font-bold">{data?.eos?.meetings?.this_month || 0}</p>
                  </div>
                </div>
                <div className="mt-4 flex justify-end">
                  <Button variant="outline" onClick={() => navigate('/eos/dashboard')}>
                    View EOS Hub
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* People / Team Overview */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Team Overview
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="text-sm text-muted-foreground">Total Users</p>
                <p className="text-2xl font-bold">{data?.people?.total_users || 0}</p>
              </div>
              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="text-sm text-muted-foreground">Active Users</p>
                <p className="text-2xl font-bold">{data?.people?.active_users || 0}</p>
              </div>
              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="text-sm text-muted-foreground">Total Teams</p>
                <p className="text-2xl font-bold">{data?.people?.total_teams || 0}</p>
              </div>
              <div className="p-4 bg-muted/50 rounded-lg text-center">
                <Button variant="link" onClick={() => navigate('/settings/users')}>
                  Manage Users
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Navigation */}
        <Card>
          <CardHeader>
            <CardTitle>Quick Navigation</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              <Button variant="outline" className="h-auto flex-col gap-2 py-4" onClick={() => navigate('/sales/dashboard')}>
                <Target className="h-6 w-6" />
                <span>Sales Hub</span>
              </Button>
              <Button variant="outline" className="h-auto flex-col gap-2 py-4" onClick={() => navigate('/operations/dashboard')}>
                <Briefcase className="h-6 w-6" />
                <span>Operations Hub</span>
              </Button>
              <Button variant="outline" className="h-auto flex-col gap-2 py-4" onClick={() => navigate('/call-center')}>
                <Phone className="h-6 w-6" />
                <span>Call Center</span>
              </Button>
              <Button variant="outline" className="h-auto flex-col gap-2 py-4" onClick={() => navigate('/eos/dashboard')}>
                <Shield className="h-6 w-6" />
                <span>EOS Hub</span>
              </Button>
              <Button variant="outline" className="h-auto flex-col gap-2 py-4" onClick={() => navigate('/marketing/dashboard')}>
                <Megaphone className="h-6 w-6" />
                <span>Marketing Hub</span>
              </Button>
              <Button variant="outline" className="h-auto flex-col gap-2 py-4" onClick={() => navigate('/settings')}>
                <Users className="h-6 w-6" />
                <span>Settings</span>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default NewCEODashboard;
